import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState, type FormEvent } from "react"
import { api, toApiMessage } from "../../api/client"
import type { FamilyRole, Invitation } from "../../api/types"
import { Icon } from "../../components/Icon"
import { Button, Field, Sheet, showToast } from "../../components/ui"
import { useFamily } from "../../family/FamilyContext"
import { copyText } from "../../lib/clipboard"
import { formatDate } from "../../lib/format"

type InviteRole = Exclude<FamilyRole, "OWNER">

export function InviteMemberSheet({ open, onClose }: { open: boolean; onClose(): void }) {
  const { activeFamilyId, activeFamily } = useFamily()
  const queryClient = useQueryClient()
  const [values, setValues] = useState({
    displayName: "",
    role: "MEMBER" as InviteRole,
    expiresInHours: 168
  })
  const [result, setResult] = useState<{ invitation: Invitation; token: string } | null>(null)
  const [error, setError] = useState("")
  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ invitation: Invitation; token: string }>(`/families/${activeFamilyId}/invitations`, {
        ...(values.displayName.trim() ? { displayName: values.displayName.trim() } : {}),
        expiresInHours: values.expiresInHours,
        role: activeFamily?.role === "OWNER" ? values.role : "MEMBER"
      }),
    onSuccess: async (data) => {
      setResult(data)
      await queryClient.invalidateQueries({ queryKey: ["family", activeFamilyId, "invitations"] })
    }
  })
  const link = result ? `${window.location.origin}/invite/${encodeURIComponent(result.token)}` : ""
  const inviteRoles: Array<[InviteRole, string]> =
    activeFamily?.role === "OWNER"
      ? [
          ["ADMIN", "Администратор"],
          ["MEMBER", "Участник"]
        ]
      : [["MEMBER", "Участник"]]

  function close() {
    setResult(null)
    setError("")
    mutation.reset()
    onClose()
  }

  async function copy() {
    const copied = await copyText(link)
    showToast(copied ? "Ссылка скопирована" : "Выделите и скопируйте ссылку вручную", copied ? "success" : "neutral")
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError("")
    mutation.mutate(undefined, { onError: (value) => setError(toApiMessage(value)) })
  }

  return (
    <Sheet
      open={open}
      onClose={close}
      title={result ? "Приглашение готово" : "Пригласить в семью"}
      description={result ? "Отправьте одноразовую ссылку человеку лично." : "Создайте ссылку для нового участника."}
    >
      {result ? (
        <div className="invite-result">
          <span className="invite-result__icon" aria-hidden="true">
            💌
          </span>
          <p>Ссылка действует до {formatDate(result.invitation.expiresAt)}.</p>
          <div className="copy-field">
            <input aria-label="Ссылка приглашения" readOnly value={link} />
            <Button size="icon" onClick={() => void copy()} aria-label="Скопировать ссылку">
              <Icon name="copy" />
            </Button>
          </div>
          <div className="dialog__actions">
            <Button onClick={close}>Готово</Button>
          </div>
        </div>
      ) : (
        <form className="form-stack" onSubmit={submit}>
          <Field label="Имя (необязательно)" hint="Можно оставить пустым — участник введёт его сам">
            <input
              autoFocus
              maxLength={80}
              value={values.displayName}
              onChange={(event) => setValues({ ...values, displayName: event.target.value })}
              placeholder="Например, Маша"
            />
          </Field>
          <Field label="Роль">
            <select
              value={values.role}
              onChange={(event) => setValues({ ...values, role: event.target.value as InviteRole })}
            >
              {inviteRoles.map(([role, label]) => (
                <option value={role} key={role}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Срок ссылки">
            <select
              value={values.expiresInHours}
              onChange={(event) => setValues({ ...values, expiresInHours: Number(event.target.value) })}
            >
              <option value={24}>1 день</option>
              <option value={168}>7 дней</option>
              <option value={336}>14 дней</option>
              <option value={720}>30 дней</option>
            </select>
          </Field>
          <div className="info-callout">
            <Icon name="lock" size={18} />
            <p>Ссылка не отправляется автоматически. Скопируйте её в удобный семейный чат.</p>
          </div>
          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={close}>
              Отмена
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Создать ссылку
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  )
}
